import { Link } from 'react-router-dom'
import { SHOW_REPLENISHMENT } from '../../navigation/constants'
import { isoToWallClock } from '../../utils/warehouseDates'

const EMPTY = '—'

const KIND_KEYS = {
  box: 'pending_prints.kind.box',
  pallet: 'pending_prints.kind.pallet',
}

const STYLES = {
  printCode: { fontSize: 12.5, fontWeight: 600, letterSpacing: '.07em', color: '#07091A' },
  barcode: { fontSize: 12.5, color: '#07091A' },
  sub: { fontSize: 11.5, color: '#8388AE', marginTop: 2 },
  lastPrinted: { color: '#8388AE' },
  reprint: { fontSize: 12, fontWeight: 600, padding: '6px 12px', borderRadius: 8, border: '1px solid #D8DAE8', color: '#1F39FF' },
}

export const PAGE_SIZE = 25

// Print job origin types the history can filter by; a new origin is one more line.
export const ORIGIN_TYPE_OPTIONS = [{ id: 'replenishment', label: 'pending_prints.origin.replenishment' }]

export const FILTERS_INITIAL_DATA = [
  { key: 'print_code', label: 'pending_prints.filters.print_code', data: { visible: true, type: 'text', value: '' } },
  { key: 'barcode', label: 'pending_prints.filters.barcode', data: { visible: false, type: 'text', value: '' } },
  {
    key: 'store_name',
    label: 'pending_prints.filters.store',
    placeholder: 'pending_prints.filters.store_placeholder',
    data: { visible: false, type: 'text', value: '' },
  },
  { key: 'origin_type', label: 'pending_prints.filters.origin', data: { visible: false, type: 'select', value: undefined, options: ORIGIN_TYPE_OPTIONS } },
  {
    key: 'origin_id',
    label: 'pending_prints.filters.origin_id',
    placeholder: 'pending_prints.filters.origin_id_placeholder',
    data: { visible: false, type: 'text', value: '' },
  },
  {
    key: 'kind',
    label: 'pending_prints.filters.kind',
    data: {
      visible: false,
      type: 'select',
      value: undefined,
      options: [
        { id: 'box', label: 'pending_prints.kind.box' },
        { id: 'pallet', label: 'pending_prints.kind.pallet' },
      ],
    },
  },
  { key: 'unprinted', label: 'pending_prints.filters.unprinted', data: { visible: false, type: 'boolean', onClickValue: true } },
  { key: 'last_printed', label: 'pending_prints.filters.last_printed', data: { visible: false, type: 'date', dateOnly: true, value: undefined } },
]

// NewTable's date-only filter hands back moments (antd RangePicker); the list filters by whole warehouse-local days.
const toDay = value => (value && typeof value.format === 'function' ? value.format('YYYY-MM-DD') : undefined)

const toDayRange = value => {
  const from = toDay(value?.from)
  const to = toDay(value?.to)
  return from && to ? { from, to } : undefined
}

const FILTER_READERS = {
  print_code: data => ['printCode', data.value || undefined],
  barcode: data => ['barcode', data.value || undefined],
  store_name: data => ['storeName', data.value || undefined],
  origin_type: data => ['originType', data.value || undefined],
  origin_id: data => ['originId', data.value || undefined],
  kind: data => ['kind', data.value || undefined],
  unprinted: data => ['unprinted', data.value === true || undefined],
  last_printed: data => ['lastPrinted', toDayRange(data.value)],
}

export const applyFilterChange = (filters, key, data) => {
  const reader = FILTER_READERS[key]
  if (!reader) return filters
  const [name, value] = reader(data || {})
  const next = { ...filters }
  if (data?.visible && value !== undefined) next[name] = value
  else delete next[name]
  return next
}

export const formatLastPrinted = (isoString, timeZone) => {
  const wallClock = isoToWallClock(isoString, timeZone)
  if (!wallClock) return EMPTY
  const [, year, month, day, time] = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}:\d{2})$/.exec(wallClock) || []
  if (!year) return EMPTY
  return `${Number(day)}/${Number(month)}/${year} ${time}`
}

export const buildColumns = () => [
  { Header: 'pending_prints.table.print_code', accessor: 'print_code', disableSortBy: true },
  { Header: 'pending_prints.table.what', accessor: 'what', disableSortBy: true },
  { Header: 'pending_prints.table.kind', accessor: 'kind', disableSortBy: true },
  { Header: 'pending_prints.table.origin', accessor: 'origin', disableSortBy: true },
  { Header: 'pending_prints.table.store', accessor: 'store', disableSortBy: true },
  { Header: 'pending_prints.table.last_printed', accessor: 'last_printed_at' },
  { Header: '', accessor: 'actions', disableSortBy: true, shrinkToContent: true },
]

// Plain-text kind and origin, shared with the store-choice cards; unknown values render raw.
export const kindLabel = (kind, t) => (KIND_KEYS[kind] ? t(KIND_KEYS[kind]) : kind)

export const originText = (type, id, t) => {
  if (!type) return null
  const label = type === 'replenishment' ? t('pending_prints.origin.replenishment') : type
  return id === null || id === undefined || id === '' ? label : `${label} #${id}`
}

const Origin = ({ origin, warehouseId, t }) => {
  if (origin?.type === 'replenishment') {
    return (
      <div>
        <div>{t('pending_prints.origin.replenishment')}</div>
        <Link to={SHOW_REPLENISHMENT.replace(':warehouse_id', warehouseId).replace(':id', origin.id)} className="block hover:underline" style={STYLES.sub}>
          {t('pending_prints.origin.replenishment_id', { id: origin.id })}
        </Link>
      </div>
    )
  }
  return originText(origin?.type, origin?.id, t) || EMPTY
}

export const buildRows = (jobs, { warehouseId, timeZone, t, onReprint, reprintDisabled }) =>
  (jobs || []).map(job => ({
    object: job,
    print_code: (
      <span className="font-mono" style={STYLES.printCode}>
        {job.print_code}
      </span>
    ),
    what: (
      <div>
        <div className="font-mono" style={STYLES.barcode}>
          {job.barcode}
        </div>
        <div style={STYLES.sub}>{t('pending_prints.what.one_label')}</div>
      </div>
    ),
    kind: kindLabel(job.kind, t),
    origin: <Origin origin={job.origin} warehouseId={warehouseId} t={t} />,
    store: job.store?.name || EMPTY,
    last_printed_at: <span style={STYLES.lastPrinted}>{formatLastPrinted(job.last_printed_at, timeZone)}</span>,
    actions: (
      <div className="flex justify-end">
        <button
          type="button"
          className="bg-white whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
          style={STYLES.reprint}
          disabled={reprintDisabled}
          onClick={() => onReprint(job)}
        >
          {job.last_printed_at ? t('pending_prints.actions.reprint') : t('pending_prints.actions.print')}
        </button>
      </div>
    ),
  }))
